export type RecordCandidatePreviewPayload = {
    candidateId: string;
    status: "pending_review" | "provisional" | "rejected";
    operations: Array<{
        position: number;
        kind:
            | "wiki_entry_create"
            | "wiki_entry_update"
            | "wiki_connection_create"
            | "plot_thread_create"
            | "plot_thread_update"
            | "plot_event_create"
            | "plot_event_update";
        targetLabel: string | null;
        payload: Record<string, string | null>;
        previousPayload: Record<string, string | null> | null;
        verificationStatus: "verified" | "provisional" | "rejected";
        verificationIssues: Array<
            | "evidence_missing"
            | "evidence_stale"
            | "coverage_incomplete"
            | "operation_unsupported"
            | "operation_contradicted"
            | "operation_ambiguous"
            | "target_ineligible"
        >;
    }>;
};

const RECORD_CANDIDATE_KINDS = new Set([
    "wiki_entry_create",
    "wiki_entry_update",
    "wiki_connection_create",
    "plot_thread_create",
    "plot_thread_update",
    "plot_event_create",
    "plot_event_update",
]);
const RECORD_CANDIDATE_STATUSES = new Set([
    "pending_review",
    "provisional",
    "rejected",
]);
const RECORD_VERIFICATION_STATUSES = new Set([
    "verified",
    "provisional",
    "rejected",
]);
const RECORD_VERIFICATION_ISSUES = new Set([
    "evidence_missing",
    "evidence_stale",
    "coverage_incomplete",
    "operation_unsupported",
    "operation_contradicted",
    "operation_ambiguous",
    "target_ineligible",
]);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;
const MAX_TARGET_LABEL_CHARACTERS = 240;

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Parses the prose-safe preview shape shared by SSE, retries, and history. */
export function parseRecordCandidatePreviewPayload(
    value: unknown,
): RecordCandidatePreviewPayload | null {
    if (!isRecord(value) || !UUID_RE.test(String(value.candidateId ?? ""))) return null;
    if (!RECORD_CANDIDATE_STATUSES.has(String(value.status ?? ""))) return null;
    if (!Array.isArray(value.operations) || value.operations.length < 1 || value.operations.length > 2) {
        return null;
    }
    const operations: RecordCandidatePreviewPayload["operations"] = [];
    for (let index = 0; index < value.operations.length; index += 1) {
        const operation = value.operations[index];
        if (
            !isRecord(operation) ||
            operation.position !== index + 1 ||
            !RECORD_CANDIDATE_KINDS.has(String(operation.kind ?? "")) ||
            (operation.targetLabel !== null &&
                (typeof operation.targetLabel !== "string" ||
                    !operation.targetLabel.trim() ||
                    Array.from(operation.targetLabel).length > MAX_TARGET_LABEL_CHARACTERS)) ||
            !isRecord(operation.payload) ||
            (operation.previousPayload !== null &&
                !isRecord(operation.previousPayload)) ||
            !RECORD_VERIFICATION_STATUSES.has(String(operation.verificationStatus ?? "")) ||
            !Array.isArray(operation.verificationIssues) ||
            operation.verificationIssues.some(
                (issue) =>
                    typeof issue !== "string" || !RECORD_VERIFICATION_ISSUES.has(issue),
            )
        ) {
            return null;
        }
        const payload = Object.fromEntries(
            Object.entries(operation.payload).filter(
                (entry): entry is [string, string | null] =>
                    entry[1] === null || typeof entry[1] === "string",
            ),
        );
        const rawPreviousPayload = operation.previousPayload ?? null;
        const previousPayload = rawPreviousPayload === null
            ? null
            : Object.fromEntries(
                  Object.entries(rawPreviousPayload).filter(
                      (entry): entry is [string, string | null] =>
                          entry[1] === null || typeof entry[1] === "string",
                  ),
              );
        if (Object.keys(payload).length !== Object.keys(operation.payload).length) return null;
        if (
            rawPreviousPayload !== null &&
            previousPayload !== null &&
            Object.keys(previousPayload).length !== Object.keys(rawPreviousPayload).length
        ) return null;
        const isUpdate = String(operation.kind).endsWith("_update");
        if (isUpdate && !String(operation.targetLabel ?? "").trim()) return null;
        if (!isUpdate && operation.targetLabel !== null) return null;
        // Candidates persisted before previous_payload existed remain readable.
        // New writes are constrained by the runtime contract and database trigger,
        // while legacy updates degrade to a proposed-only preview.
        if (!isUpdate && previousPayload !== null) return null;
        if (
            previousPayload &&
            Object.keys(previousPayload).sort().join("\u0000") !==
                Object.keys(payload).sort().join("\u0000")
        ) return null;
        operations.push({
            position: operation.position,
            kind: operation.kind as RecordCandidatePreviewPayload["operations"][number]["kind"],
            targetLabel: operation.targetLabel as string | null,
            payload,
            previousPayload,
            verificationStatus: operation.verificationStatus as RecordCandidatePreviewPayload["operations"][number]["verificationStatus"],
            verificationIssues: [...operation.verificationIssues] as RecordCandidatePreviewPayload["operations"][number]["verificationIssues"],
        });
    }
    return {
        candidateId: value.candidateId as string,
        status: value.status as RecordCandidatePreviewPayload["status"],
        operations,
    };
}
